// ============================================================================
// KLINISOL CRO - User Form Component (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// Add/Edit User Modal - FDA 21 CFR Part 11 Compliant
// ============================================================================

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
    X, 
    UserPlus, 
    UserCog, 
    CheckCircle, 
    Copy, 
    AlertTriangle, 
    Info,
    Loader2,
    Mail,
    User,
    Shield,
    Building,
    Phone
} from 'lucide-react';

const UserForm = ({ user, roles, onClose }) => {
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        roleId: '',
        department: '',
        phone: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [tempPassword, setTempPassword] = useState(null);

    // ========================================================================
    // INITIALIZE FORM
    // ========================================================================
    
    useEffect(() => {
        if (user) {
            setFormData({
                email: user.email || '',
                firstName: user.first_name || '',
                lastName: user.last_name || '',
                roleId: user.role_id || '',
                department: user.department || '',
                phone: user.phone || ''
            });
        }
    }, [user]);

    // ========================================================================
    // VALIDATION
    // ========================================================================
    
    const validateForm = () => {
        const newErrors = {};

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!formData.roleId) {
            newErrors.roleId = 'Role is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ========================================================================
    // HANDLERS
    // ========================================================================
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            let response;
            
            if (user) {
                // Update existing user
                response = await api.put(`/users/${user.id}`, formData);
            } else {
                // Create new user
                response = await api.post('/users', formData);
            }

            if (response.data.success) {
                if (!user && response.data.data.temporaryPassword) {
                    // Show temporary password for new user
                    setTempPassword(response.data.data.temporaryPassword);
                } else {
                    alert(user ? 'User updated successfully' : 'User created successfully');
                    onClose(true); // true = refresh user list
                }
            }
        } catch (error) {
            console.error('Form submission error:', error);
            alert(error.response?.data?.message || 'Operation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyPassword = () => {
        navigator.clipboard.writeText(tempPassword);
        alert('Password copied to clipboard!');
    };

    const handlePasswordModalClose = () => {
        setTempPassword(null);
        onClose(true); // true = refresh user list
    };

    // ========================================================================
    // RENDER - TEMPORARY PASSWORD DISPLAY
    // ========================================================================
    
    if (tempPassword) {
        return (
            <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fadeIn" onClick={handlePasswordModalClose}>
                <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
                    {/* Success Header */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-7 rounded-t-3xl flex items-center gap-4 text-white">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <CheckCircle className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="text-2xl font-black">User Created Successfully</h2>
                    </div>
                    
                    <div className="p-8">
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-6 mb-6">
                            <p className="text-base text-green-900">
                                <strong>{formData.firstName} {formData.lastName}</strong> has been added to the system.
                            </p>
                        </div>

                        <div className="mb-6">
                            <label className="flex items-center gap-2 text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
                                <Shield className="w-5 h-5 text-[#475569]" />
                                Temporary Password (Give this to the user)
                            </label>
                            <div className="bg-gray-900 rounded-xl p-4 flex items-center justify-between gap-4 border-2 border-gray-700">
                                <code className="text-xl font-mono font-bold text-green-400 tracking-wider flex-1">
                                    {tempPassword}
                                </code>
                                <button 
                                    onClick={handleCopyPassword}
                                    className="px-4 py-2 bg-[#475569] text-white rounded-lg hover:bg-[#334155] transition-all flex items-center gap-2 font-semibold"
                                    title="Copy to clipboard"
                                >
                                    <Copy className="w-4 h-4" />
                                    Copy
                                </button>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 rounded-xl p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                                <h4 className="text-base font-bold text-orange-900">Important Instructions:</h4>
                            </div>
                            <ol className="space-y-2 text-sm text-orange-900 pl-9 list-decimal list-outside">
                                <li>Copy this password and give it to the user securely</li>
                                <li>User <strong>MUST</strong> change this password on first login</li>
                                <li>This password will not be shown again</li>
                                <li>User email: <strong>{formData.email}</strong></li>
                            </ol>
                        </div>
                    </div>

                    <div className="p-6 border-t-2 border-gray-200 flex justify-end">
                        <button 
                            onClick={handlePasswordModalClose}
                            className="px-8 py-3.5 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all shadow-lg"
                        >
                            I've Copied the Password - Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ========================================================================
    // RENDER - USER FORM
    // ========================================================================
    
    return (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fadeIn" onClick={() => onClose(false)}>
            <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-auto shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-[#475569] to-[#334155] p-7 rounded-t-3xl flex justify-between items-center text-white">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            {user ? (
                                <UserCog className="w-6 h-6 text-white" />
                            ) : (
                                <UserPlus className="w-6 h-6 text-white" />
                            )}
                        </div>
                        <h2 className="text-2xl font-black">
                            {user ? 'Edit User' : 'Add New User'}
                        </h2>
                    </div>
                    <button 
                        onClick={() => onClose(false)}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-all flex items-center justify-center"
                        disabled={loading}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-8">
                        {/* Email */}
                        <div className="mb-6">
                            <label className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                                <Mail className="w-4 h-4 text-[#475569]" />
                                Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed ${
                                    errors.email 
                                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                        : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                                }`}
                                disabled={loading}
                                placeholder="user@klinisol.com"
                            />
                            {errors.email && (
                                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                    <AlertTriangle className="w-4 h-4" />
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        {/* First Name & Last Name */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                                    <User className="w-4 h-4 text-[#475569]" />
                                    First Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed ${
                                        errors.firstName 
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                            : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                                    }`}
                                    disabled={loading}
                                    placeholder="John"
                                />
                                {errors.firstName && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                        <AlertTriangle className="w-4 h-4" />
                                        {errors.firstName}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                                    <User className="w-4 h-4 text-[#475569]" />
                                    Last Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed ${
                                        errors.lastName 
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                            : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                                    }`}
                                    disabled={loading}
                                    placeholder="Doe"
                                />
                                {errors.lastName && (
                                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                        <AlertTriangle className="w-4 h-4" />
                                        {errors.lastName}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Role */}
                        <div className="mb-6">
                            <label className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                                <Shield className="w-4 h-4 text-[#475569]" />
                                Role <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="roleId"
                                value={formData.roleId}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 disabled:opacity-50 disabled:cursor-not-allowed ${
                                    errors.roleId 
                                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                        : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                                }`}
                                disabled={loading}
                            >
                                <option value="">Select Role</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.id}>
                                        {role.role_name} - {role.description}
                                    </option>
                                ))}
                            </select>
                            {errors.roleId && (
                                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                    <AlertTriangle className="w-4 h-4" />
                                    {errors.roleId}
                                </p>
                            )}
                        </div>

                        {/* Department & Phone */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                                    <Building className="w-4 h-4 text-gray-600" />
                                    Department <span className="text-gray-500 text-xs">(Optional)</span>
                                </label>
                                <input
                                    type="text"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loading}
                                    placeholder="Clinical Operations"
                                />
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                                    <Phone className="w-4 h-4 text-gray-600" />
                                    Phone <span className="text-gray-500 text-xs">(Optional)</span>
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loading}
                                    placeholder="+1 (555) 123-4567"
                                />
                            </div>
                        </div>

                        {/* Info Box for New Users */}
                        {!user && (
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-[#475569] rounded-xl p-5">
                                <div className="flex gap-3 items-start">
                                    <Info className="w-5 h-5 text-[#475569] flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-[#475569] leading-relaxed">
                                        <strong>Note:</strong> A secure temporary password will be generated automatically. 
                                        The user must change it on first login.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Modal Footer */}
                    <div className="p-6 border-t-2 border-gray-200 flex gap-4 flex-wrap justify-end">
                        <button 
                            type="button"
                            onClick={() => onClose(false)}
                            className="px-8 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit"
                            className={`px-8 py-3.5 rounded-xl font-bold transition-all flex items-center gap-2 ${
                                loading
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-gradient-to-r from-[#475569] to-[#334155] text-white hover:-translate-y-0.5 shadow-lg'
                            }`}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span>Processing...</span>
                                </>
                            ) : (
                                <span>{user ? 'Update User' : 'Create User'}</span>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default UserForm;